import { motion } from "framer-motion";
import { Compass, DraftingCompass, Cpu, ClipboardCheck, Rocket } from "lucide-react";

const steps = [
  { icon: Compass, num: "01", title: "الاكتشاف", desc: "جلسة لفهم الهدف والجمهور ومعايير النجاح قبل أي سطر برمجي." },
  { icon: DraftingCompass, num: "02", title: "المتطلبات والعرض", desc: "نطاق واضح، مراحل، مدة، وسعر نهائي معلن قبل البدء." },
  { icon: Cpu, num: "03", title: "التصميم والبناء", desc: "تسليمات مرحلية تراها وتجربها بنفسك، مدعومة بسرعة الذكاء الاصطناعي." },
  { icon: ClipboardCheck, num: "04", title: "الاختبار والقبول", desc: "اختبار شامل على الجوال والحاسب وفق معايير قبول متفق عليها." },
  { icon: Rocket, num: "05", title: "الإطلاق والدعم", desc: "نشر وتسليم وتدريب، مع 14 يوم دعم مجاني بعد الإطلاق." },
];

export const Methodology = () => (
  <section id="methodology" data-testid="methodology-section" className="relative py-24 sm:py-32 bg-[#0B132B]">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7 }}
        className="mb-14"
      >
        <div className="font-mono-tech text-xs sm:text-sm tracking-[0.25em] text-[#D4AF37] mb-4" dir="ltr">— OUR PROCESS</div>
        <h2 className="font-display font-extrabold text-2xl sm:text-3xl lg:text-4xl text-[#F8FAFC]" data-testid="methodology-title">
          منهجية العمل <span className="gold-text">من الفكرة إلى الإطلاق</span>
        </h2>
        <p className="mt-4 font-body text-base sm:text-lg text-[#94A3B8]" data-testid="methodology-subtitle">
          خمس خطوات واضحة نمشيها معك من أول جلسة حتى ما بعد الإطلاق:
        </p>
      </motion.div>

      <div className="relative">
        <div className="hidden lg:block absolute top-[52px] right-0 left-0 h-px bg-gradient-to-l from-transparent via-[#D4AF37]/35 to-transparent" aria-hidden="true" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
          {steps.map((s, i) => (
            <motion.div
              key={s.num}
              initial={{ opacity: 0, y: 36 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
              className="relative group"
              data-testid={`methodology-step-${i + 1}`}
            >
              <div className="flex items-center gap-4 lg:flex-col lg:items-start">
                <div className="relative z-10 w-[104px] h-[104px] rounded-3xl glass-card flex flex-col items-center justify-center gap-1 group-hover:border-[#D4AF37]/50 transition-all duration-300 shrink-0">
                  <s.icon size={26} className="text-[#E5C158]" />
                  <span className="font-mono-tech text-xs text-[#D4AF37]/70" dir="ltr">{s.num}</span>
                </div>
              </div>
              <h3 className="mt-5 font-display font-bold text-lg text-[#F8FAFC]">{s.title}</h3>
              <p className="mt-2 font-body text-sm leading-relaxed text-[#94A3B8]">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, delay: 0.3 }}
        className="mt-16 glass-card rounded-3xl p-7 sm:p-9"
        data-testid="guarantees-bar"
      >
        <div className="font-display font-bold text-[#E5C158] mb-5">ضماناتنا لك:</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            "تعديلات مجانية ضمن النطاق المتفق عليه",
            "سرية كاملة لبياناتك وفكرتك",
            "تسليم الملكية والشيفرة والوثائق كاملة",
            "14 يوم دعم مجاني بعد الإطلاق",
          ].map((g) => (
            <div key={g} className="flex items-start gap-3 font-body text-sm sm:text-base text-[#CBD5E1]">
              <span className="mt-1.5 w-2 h-2 rounded-full bg-[#D4AF37] shrink-0" />
              {g}
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  </section>
);
